<template>
<div class="rqp-page">
  <div class="rqp-toolbar no-print">
    <button class="btn btn-sm btn-success" @click="doPrint"><i class="mdi mdi-printer me-1"></i>Print / Save as PDF</button>
    <doc-share v-if="rq && !sharedView" class="ms-2" :title="'Requisition — ' + (rq.farm_name || '')" :doc-ref="rq.document_ref" doc-type="requisition" :doc-id="rq.id" />
    <button class="btn btn-sm btn-outline-secondary ms-2" @click="$router.go(-1)"><i class="mdi mdi-arrow-left me-1"></i>Back</button>
  </div>

  <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

  <div v-else-if="rq" class="rqp-sheet">
    <!-- Header -->
    <header class="rqp-header">
      <div class="rqp-letterhead">
        <h1 class="rqp-farm-name">{{ rq.farm_name || '' }}</h1>
        <p class="rqp-farm-meta">{{ rq.farm_address || '' }}<span v-if="rq.farm_county"> — {{ rq.farm_county }} County</span></p>
        <p v-if="rq.farm_phone" class="rqp-farm-meta">Tel: {{ rq.farm_phone }}</p>
      </div>
      <div class="rqp-status-block">
        <span class="rqp-status" :class="'st-'+rq.status">{{ statusLabel(rq.status) }}</span>
        <div class="rqp-ref">{{ rq.document_ref }}</div>
      </div>
    </header>

    <h2 class="rqp-title">REQUISITION</h2>

    <div class="rqp-meta-grid">
      <div><span class="rqp-label">Project</span><strong>{{ rq.project_code || '—' }}</strong></div>
      <div><span class="rqp-label">Activity</span><strong>{{ rq.activity || '—' }}</strong></div>
      <div><span class="rqp-label">Date</span><strong>{{ fmtDate(rq.requisition_date) }}</strong></div>
    </div>

    <table class="rqp-table">
      <thead>
        <tr><th style="width:120px;">Date</th><th>Item</th><th style="width:160px;text-align:right;">Amount (KES)</th></tr>
      </thead>
      <tbody>
        <tr v-for="it in rq.items" :key="'it-'+it.id">
          <td>{{ fmtDate(it.item_date) }}</td>
          <td>{{ it.description }}</td>
          <td class="rqp-amount">{{ Number(it.amount_kes).toLocaleString() }}</td>
        </tr>
        <tr v-if="!rq.items || !rq.items.length">
          <td colspan="3" style="text-align:center;color:#999;padding:14px;">No line items</td>
        </tr>
      </tbody>
      <tfoot>
        <tr><td colspan="2" class="rqp-total-label">TOTAL</td><td class="rqp-amount rqp-total-amount">KES {{ Number(rq.total_kes||0).toLocaleString() }}</td></tr>
      </tfoot>
    </table>

    <div v-if="rq.notes" class="rqp-notes"><strong>Notes:</strong> {{ rq.notes }}</div>

    <div class="rqp-sign-grid">
      <div class="rqp-sign-block">
        <div class="rqp-sign-role">Prepared by</div>
        <div class="rqp-sign-name">{{ rq.prepared_by_name || '_______________________' }}</div>
        <div class="rqp-sign-date">Date: {{ fmtDate(rq.requisition_date) }}</div>
      </div>
      <div class="rqp-sign-block">
        <div class="rqp-sign-role">Approved by</div>
        <div class="rqp-sign-name">
          <span v-if="rq.status === 'approved' || rq.status === 'paid'">{{ rq.approved_by_name || '_______________________' }}</span>
          <span v-else>_______________________</span>
        </div>
        <div class="rqp-sign-date">Date: {{ rq.approved_at ? fmtDate(rq.approved_at) : '_______________________' }}</div>
      </div>
    </div>

    <div v-if="rq.status === 'rejected' && rq.rejection_reason" class="rqp-rejected">
      <strong>REJECTED:</strong> {{ rq.rejection_reason }}
    </div>

    <div v-if="rq.status === 'paid'" class="rqp-paid">
      <i class="mdi mdi-check-circle me-1"></i>PAID <span style="opacity:.7;font-weight:400;"> · {{ rq.paid_at ? fmtDate(rq.paid_at) : '' }}</span>
    </div>
  </div>
</div>
</template>

<style>
.rqp-page { background:#F0EDE5; min-height:100vh; padding:30px 16px; color:#1A1A1A; font-family:Arial, sans-serif; }
.rqp-toolbar { max-width:780px; margin:0 auto 16px; display:flex; justify-content:center; }
.rqp-sheet { max-width:780px; margin:0 auto; background:#FFFFFF; border:1px solid #D8D2C2; padding:36px 40px; box-shadow:0 4px 24px rgba(0,0,0,.08); }

.rqp-header { display:flex; justify-content:space-between; align-items:flex-start; padding-bottom:14px; border-bottom:2px solid #1A1A1A; margin-bottom:18px; }
.rqp-farm-name { font-family:Georgia,serif; color:#1A1A1A; font-size:22px; margin:0; font-weight:700; }
.rqp-farm-meta { font-size:11px; color:#555; margin:3px 0 0; line-height:1.5; }
.rqp-status-block { text-align:right; }
.rqp-status { font-size:10px; font-weight:700; padding:4px 12px; border-radius:3px; text-transform:uppercase; letter-spacing:1.2px; display:inline-block; }
.rqp-status.st-draft { background:#E0DDD3; color:#777; }
.rqp-status.st-submitted { background:#D6E9FF; color:#0F4C81; }
.rqp-status.st-approved { background:#C8E6C9; color:#1F6B1F; }
.rqp-status.st-rejected { background:#FFCDD2; color:#B71C1C; }
.rqp-status.st-paid { background:#FFE0B2; color:#8B5A2B; }
.rqp-ref { font-family:'Courier New',monospace; color:#555; font-size:11px; margin-top:6px; }

.rqp-title { font-family:Georgia,serif; text-align:center; letter-spacing:3px; margin:14px 0 22px; font-size:22px; color:#1A1A1A; }

.rqp-meta-grid { display:grid; grid-template-columns:1fr 2fr 1fr; gap:14px; margin-bottom:18px; padding:10px 14px; background:#F7F5F0; border:1px solid #E0DDD3; border-radius:3px; }
.rqp-meta-grid > div { display:flex; flex-direction:column; }
.rqp-label { font-size:10px; color:#888; text-transform:uppercase; letter-spacing:1px; margin-bottom:3px; }
.rqp-meta-grid strong { font-size:13px; color:#1A1A1A; }

.rqp-table { width:100%; border-collapse:collapse; margin:14px 0; }
.rqp-table thead th { background:#1A1A1A; color:#FFF; padding:9px 12px; text-align:left; font-size:11px; letter-spacing:1px; font-weight:700; }
.rqp-table tbody td { padding:9px 12px; border-bottom:1px solid #E0DDD3; font-size:12px; }
.rqp-amount { text-align:right; font-family:'Courier New',monospace; }
.rqp-total-label { text-align:right; font-weight:700; padding:11px 12px; background:#F7F5F0; }
.rqp-total-amount { background:#F7F5F0; font-weight:700; font-size:13px; }

.rqp-notes { font-size:11px; color:#555; padding:10px 12px; background:#F7F5F0; border-left:3px solid #1A1A1A; margin-top:14px; }

.rqp-sign-grid { display:grid; grid-template-columns:1fr 1fr; gap:30px; margin-top:30px; padding-top:18px; border-top:1px dashed #D8D2C2; }
.rqp-sign-block { font-family:'Courier New',monospace; font-size:11px; color:#1A1A1A; }
.rqp-sign-role { font-size:10px; color:#888; text-transform:uppercase; letter-spacing:1px; margin-bottom:6px; }
.rqp-sign-name { font-size:13px; font-weight:700; margin-bottom:4px; min-height:16px; }
.rqp-sign-date { font-size:11px; color:#555; }

.rqp-rejected { background:#FFCDD2; color:#B71C1C; padding:10px 14px; margin-top:18px; font-size:12px; border-radius:3px; }
.rqp-paid { background:#C8E6C9; color:#1F6B1F; padding:10px 14px; margin-top:18px; font-size:13px; text-align:center; font-weight:700; letter-spacing:1.5px; border-radius:3px; }

@media print {
  .no-print { display:none !important; }
  .rqp-page { background:#fff; padding:0; }
  .rqp-sheet { box-shadow:none; border:none; max-width:none; }
}

/* On-screen mobile preview */
@media (max-width: 768px) {
  .rqp-page { padding: 12px; }
  .rqp-sheet { padding: 16px; border-radius: 6px; }
  .rqp-sheet h1, .rqp-sheet h2 { font-size: 16px; }
  .rqp-table { font-size: 11px; }
  .rqp-table td, .rqp-table th { padding: 4px 6px; }
}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker } from "@/api";
import DocShare from "@/components/flockr-farm/DocShare.vue";
export default {
  name: "RequisitionPrint",
  components: { DocShare },
  data(){ return { loading:true, rq:null }; },
  computed: { sharedView(){ return !!this.$route.query.t; } },
  mounted(){
    var token = this.$route.query.t;
    if (token) this.loadByToken(token);
    else this.load(this.$route.params.id);
  },
  methods:{
    fmtDate(d){ if(!d)return ''; try { return new Date(d).toLocaleDateString(undefined,{day:'2-digit',month:'short',year:'numeric'}); } catch(e){ return d; } },
    statusLabel(s){ return ({draft:'Draft',submitted:'Submitted',approved:'Approved',rejected:'Rejected',paid:'Paid'})[s] || s; },
    async load(id){
      this.loading = true;
      try {
        var r = await executeJson({ function:'getRequisition', requisitionId:id }, flocker);
        if (r.data && r.data.success) this.rq = r.data.data.requisition;
      } catch(e){ console.error(e); }
      this.loading = false;
    },
    async loadByToken(token){
      this.loading = true;
      try {
        var r = await executeJson({ function:'getSharedDocument', token: token }, flocker);
        if (r.data && r.data.success) this.rq = r.data.data.requisition;
        else alert((r.data && r.data.message) || 'This share link is no longer valid.');
      } catch(e){ console.error(e); }
      this.loading = false;
    },
    doPrint(){ window.print(); }
  }
};
</script>
